import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  createDevice,
  deleteDevice,
  CreateDevicePayload,
  getDevice,
  DeviceResponse,
  updateDevice,
  getAllDevicesTable,
  getDeviceDetails,
  DeviceDetailsResponse,
  DeviceTableData,
  PartOfDataAllDevicesResponse,
  getPartOfDataAllDevices,
} from '@app/api/device.api'
import type { RootState } from '@app/store/store'

import { DeviceUpdateRequest } from '@/features/devices/components/DeviceForm'
import { Status } from '@/types/general-types'
import { getErrorMessage } from '@/utils/utils'

import { deleteInverterData } from './inverter-slice'
import { getProfileData } from './profile-slice'
import { deleteAlarmInfoData } from './alarm-slice'
import { GetStateFn } from './types'

type DeviceState = {
  allDevicesData: Record<string, DeviceDetailsResponse>
  allDevicesStatus: Status
  devicesDetailsStatus: Status
}

const initialState: DeviceState = {
  allDevicesData: {},
  allDevicesStatus: 'idle',
  devicesDetailsStatus: 'idle',
}
// done

export const getAllDevicesTableData = createAsyncThunk<DeviceTableData[]>(
  'device/getAllDevicesTable',
  async (_, { rejectWithValue }) => {
    try {
      const res = await getAllDevicesTable()

      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const getDeviceDetailsData = createAsyncThunk<DeviceDetailsResponse, string>(
  'device/getDeviceDetails',
  async (id, { rejectWithValue }) => {
    try {
      const res = await getDeviceDetails(id)
      console.log(res, 'res')
      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const updateDeviceData = createAsyncThunk<DeviceDetailsResponse, DeviceUpdateRequest>(
  'device/updateDevice',
  async (updateDevicePayload, { rejectWithValue, dispatch, getState }) => {
    try {
      const res = await updateDevice(updateDevicePayload)

      refreshProfileIfDeviceAssigned(getState, dispatch, updateDevicePayload.id)

      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const createDeviceData = createAsyncThunk<DeviceDetailsResponse, CreateDevicePayload>(
  'device/createDevice',
  async (createDevicePayload, { rejectWithValue }) => {
    try {
      const res = await createDevice(createDevicePayload)

      return res
    } catch (err: any) {
      return rejectWithValue(err.message)
    }
  },
)

export const deleteDeviceData = createAsyncThunk<void, string>(
  'device/deleteDevice',
  async (id, { rejectWithValue, dispatch, getState }) => {
    try {
      const res = await deleteDevice(id)

      refreshProfileIfDeviceAssigned(getState, dispatch, id)

      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const getPartOfDataAllDevicesData = createAsyncThunk<PartOfDataAllDevicesResponse[]>(
  'device/getPartOfDataAllDevicesData',
  async (_, { rejectWithValue }) => {
    try {
      const res = await getPartOfDataAllDevices()

      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const getDeviceData = createAsyncThunk<DeviceResponse, string>(
  'device/getDeviceData',
  async (id, { rejectWithValue }) => {
    try {
      const res = await getDevice(id)

      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const deviceSlice = createSlice({
  name: 'device',
  initialState,
  reducers: {},

  extraReducers: (builder) => {
    builder

      .addCase(getAllDevicesTableData.pending, (state) => {
        state.allDevicesStatus = 'loading'
      })
      .addCase(getDeviceDetailsData.pending, (state) => {
        state.devicesDetailsStatus = 'loading'
      })
      .addCase(updateDeviceData.pending, (state) => {
        state.devicesDetailsStatus = 'loading'
      })
      .addCase(createDeviceData.pending, (state) => {
        state.devicesDetailsStatus = 'loading'
      })
      .addCase(deleteDeviceData.pending, (state) => {
        state.allDevicesStatus = 'loading'
      })
      .addCase(getPartOfDataAllDevicesData.pending, (state) => {
        state.allDevicesStatus = 'loading'
      })
      .addCase(getDeviceData.pending, (state) => {
        state.devicesDetailsStatus = 'loading'
      })
      .addCase(createDeviceData.rejected, (state) => {
        state.devicesDetailsStatus = 'failed'
      })
      .addCase(getAllDevicesTableData.rejected, (state) => {
        state.allDevicesStatus = 'failed'
      })
      .addCase(getDeviceDetailsData.rejected, (state) => {
        state.devicesDetailsStatus = 'failed'
      })
      .addCase(updateDeviceData.rejected, (state) => {
        state.devicesDetailsStatus = 'failed'
      })
      .addCase(deleteDeviceData.rejected, (state) => {
        state.allDevicesStatus = 'failed'
      })
      .addCase(getPartOfDataAllDevicesData.rejected, (state) => {
        state.allDevicesStatus = 'failed'
      })
      .addCase(getDeviceData.rejected, (state) => {
        state.devicesDetailsStatus = 'failed'
      })

      .addCase(getAllDevicesTableData.fulfilled, (state, action) => {
        action.payload.forEach((device) => {
          const id = device.id
          const existing = state.allDevicesData[id] ?? {}

          state.allDevicesData[id] = {
            ...existing,
            ...device,
          }
        })

        state.allDevicesStatus = 'succeeded'
      })
      .addCase(getDeviceDetailsData.fulfilled, (state, action) => {
        const { id, ...deviceDetailsData } = action.payload
        const existingDevice = state.allDevicesData[id]

        state.allDevicesData[id] = {
          ...existingDevice,
          ...deviceDetailsData,
        }

        state.devicesDetailsStatus = 'succeeded'
      })
      .addCase(updateDeviceData.fulfilled, (state, action) => {
        const { id, ...deviceDetailsData } = action.payload
        const existingData = state.allDevicesData[id]

        state.allDevicesData[id] = {
          ...existingData,
          ...deviceDetailsData,
        }

        state.devicesDetailsStatus = 'succeeded'
      })
      .addCase(createDeviceData.fulfilled, (state, action) => {
        const { id, ...deviceDetailsData } = action.payload

        state.allDevicesData = {
          [id]: { ...deviceDetailsData, id },
          ...state.allDevicesData,
        }

        state.devicesDetailsStatus = 'succeeded'
      })
      .addCase(deleteDeviceData.fulfilled, (state, action) => {
        const deletedDeviceId = action.meta.arg

        delete state.allDevicesData[deletedDeviceId]

        state.allDevicesStatus = 'succeeded'
      })
      .addCase(getPartOfDataAllDevicesData.fulfilled, (state, action) => {
        action.payload.forEach((device) => {
          const id = device.id
          const existing = state.allDevicesData[id] ?? {}

          state.allDevicesData[id] = {
            ...existing,
            ...device,
          }
        })
        state.allDevicesStatus = 'succeeded'
      })
      .addCase(getDeviceData.fulfilled, (state, action) => {
        const deviceId = action.meta.arg
        const existingDevice = state.allDevicesData[deviceId] || {}

        state.allDevicesData[deviceId] = {
          ...existingDevice,
          ...action.payload,
          id: deviceId,
        }

        state.devicesDetailsStatus = 'succeeded'
      })

      .addCase(deleteInverterData.fulfilled, (state, action) => {
        const deletedInverterId = action.payload.id

        Object.values(state.allDevicesData).forEach((device) => {
          if (!device?.inverterDeviceConnections?.length) return

          device.inverterDeviceConnections = device.inverterDeviceConnections.filter(
            ({ inverterId }) => inverterId !== deletedInverterId,
          )
        })

        state.devicesDetailsStatus = 'succeeded'
      })
      .addCase(deleteAlarmInfoData.fulfilled, (state, action) => {
        const deletedAlarmId = action.payload.id

        Object.values(state.allDevicesData).forEach((device) => {
          if (!device?.alarmDeviceConnections?.length) return

          device.alarmDeviceConnections = device.alarmDeviceConnections.filter(
            ({ alarmId }) => alarmId !== deletedAlarmId,
          )
        })

        state.devicesDetailsStatus = 'succeeded'
      })
  },
})

export default deviceSlice.reducer

const getProfileIdIfDeviceAssigned = (state: RootState, deviceId: string): string | null => {
  const profile = state.profile?.data

  if (!profile) return null

  const deviceInRoot = profile.devices.some(({ id }) => id === deviceId)
  if (deviceInRoot) return profile.id

  const deviceInFirm = profile.firms.some((firm) => firm.devices.some(({ id }) => id === deviceId))
  return deviceInFirm ? profile.id : null
}

const refreshProfileIfDeviceAssigned = (getState: GetStateFn, dispatch: (action: any) => any, deviceId: string) => {
  const profileId = getProfileIdIfDeviceAssigned(getState() as RootState, deviceId)

  if (profileId) dispatch(getProfileData(profileId))
}
